import { lazy, Suspense, useEffect, useRef, useState } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import HeroContent from "./HeroContent";
import usePerformanceMode from "./hooks/usePerformanceMode";
import { heroConfig } from "./config/heroConfig";

const HeroScene = lazy(() => import("./HeroScene"));

function SceneShellFallback() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[1]"
      aria-hidden
      data-hero-scene-loading
    />
  );
}

function SceneErrorFallback() {
  return null;
}

/**
 * Hero orchestrator — DOM content + lazy WebGL foundation.
 * Lenis / GSAP remain on the document side; Canvas pauses when off-screen.
 */
export default function Hero({ ready }) {
  const sectionRef = useRef(null);
  const perf = usePerformanceMode();
  const [inView, setInView] = useState(true);
  const [allowScene, setAllowScene] = useState(false);

  // Defer Canvas until loader finishes + browser idle to protect LCP / Lenis boot
  useEffect(() => {
    if (!ready || !perf.enableScene) {
      setAllowScene(false);
      return undefined;
    }

    let idleId;
    let timeoutId;
    const start = () => setAllowScene(true);

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(start, { timeout: 1200 });
    } else {
      timeoutId = setTimeout(start, 400);
    }

    return () => {
      if (idleId != null && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [ready, perf.enableScene]);

  useEffect(() => {
    if (!heroConfig.performance.pauseWhenOffscreen || !sectionRef.current) {
      return undefined;
    }

    const el = sectionRef.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting && entry.intersectionRatio > 0.05);
      },
      { threshold: [0, 0.05, 0.2], rootMargin: "10% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const mountScene = allowScene && perf.enableScene;

  return (
    <section
      ref={sectionRef}
      id="top"
      className="relative isolate flex min-h-screen flex-col justify-end overflow-hidden pb-20 pt-36"
      data-testid="hero-section"
      data-hero-tier={perf.tier}
      data-hero-scene={mountScene ? "on" : "off"}
    >
      {mountScene ? (
        <ErrorBoundary fallback={<SceneErrorFallback />}>
          <Suspense fallback={<SceneShellFallback />}>
            <HeroScene
              dpr={perf.dpr}
              enableShadows={perf.enableShadows}
              antialias={perf.antialias}
              powerPreference={perf.powerPreference}
              visible={inView}
            />
          </Suspense>
        </ErrorBoundary>
      ) : null}

      <HeroContent ready={ready} showPhotoBackdrop animationRootRef={sectionRef} />
    </section>
  );
}
